import { useMemo, useState } from 'react';
import ContentCard from '@/components/feature/ContentCard';
import PageHeader from '@/components/feature/PageHeader';
import { FilterControls, SortTabs, type FilterGroupConfig } from '@/components/feature/FilterControls';
import { useSeo } from '@/hooks/useSeo';
import { collectGenres, collectYears, movies } from '@/lib/contentService';

const PAGE_SIZE = 8;

export default function MoviesPage() {
  useSeo({
    title: '电影 - 玩偶姐姐影视内容平台',
    description:
      '玩偶姐姐电影频道，按类型与年份浏览热门电影、院线新片与经典佳作，包含剧情简介、主演阵容与评分参考。',
    keywords: '电影推荐,热门电影,院线新片,电影分类,玩偶姐姐电影',
  });

  const [genre, setGenre] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState('heat');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const genreOptions = useMemo(
    () => [
      { label: '全部类型', value: '全部' },
      ...collectGenres(movies).map((item) => ({ label: item, value: item })),
    ],
    [],
  );
  const yearOptions = useMemo(
    () => [
      { label: '全部年份', value: '全部' },
      ...collectYears(movies).map((item) => ({ label: `${item} 年`, value: String(item) })),
    ],
    [],
  );

  const filtered = useMemo(() => {
    const list = movies.filter((movie) => {
      const genreMatch = genre === '全部' || movie.genre.includes(genre);
      const yearMatch = year === '全部' || String(movie.year) === year;
      return genreMatch && yearMatch;
    });
    return [...list].sort((a, b) => {
      if (sort === 'year') return b.year - a.year;
      if (sort === 'rating') return (b.rating ?? 0) - (a.rating ?? 0);
      return (b.heat ?? 0) - (a.heat ?? 0);
    });
  }, [genre, year, sort]);

  const shown = filtered.slice(0, visible);

  const updateGenre = (value: string) => {
    setGenre(value);
    setVisible(PAGE_SIZE);
  };
  const updateYear = (value: string) => {
    setYear(value);
    setVisible(PAGE_SIZE);
  };
  const resetFilters = () => {
    setGenre('全部');
    setYear('全部');
    setSort('heat');
    setVisible(PAGE_SIZE);
  };

  const filterGroups: FilterGroupConfig[] = [
    { label: '类型', options: genreOptions, value: genre, onChange: updateGenre },
    { label: '年份', options: yearOptions, value: year, onChange: updateYear },
  ];

  return (
    <div className="w-full">
      <PageHeader
        eyebrow="Movies"
        title="热门电影一览"
        description="按类型与年份筛选，找到这个月最值得进影院的那一部。所有作品均附完整资料、剧情简介与编辑评分参考。"
        crumbs={[{ label: '电影' }]}
        aside={
          <div className="flex items-center gap-5 rounded-lg border border-background-300 bg-background-50 px-5 py-3">
            <div>
              <p className="font-heading text-xl font-bold leading-none text-foreground-950">{movies.length}</p>
              <p className="mt-1 font-label text-[11px] text-foreground-500">收录影片</p>
            </div>
            <span className="h-8 w-px bg-background-300"></span>
            <div>
              <p className="font-heading text-xl font-bold leading-none text-primary-600">
                {collectGenres(movies).length}
              </p>
              <p className="mt-1 font-label text-[11px] text-foreground-500">类型覆盖</p>
            </div>
          </div>
        }
      />

      <section className="w-full px-4 py-6 md:px-8 md:py-8">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="hidden md:block">
            <FilterControls
              groups={filterGroups}
              onReset={resetFilters}
              showReset
            />
          </div>

          <div className="flex items-center justify-between gap-3 md:hidden">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3.5 py-2 text-sm text-foreground-700"
            >
              <i className="ri-filter-3-line text-[15px]"></i>
              筛选
              {(genre !== '全部' || year !== '全部') && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 font-label text-[10px] text-background-50">
                  {(genre !== '全部' ? 1 : 0) + (year !== '全部' ? 1 : 0)}
                </span>
              )}
            </button>
            <span className="font-label text-xs text-foreground-500">共 {filtered.length} 部</span>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
            <p className="hidden font-label text-xs text-foreground-500 md:block">
              共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部符合条件的影片
            </p>
            <SortTabs
              value={sort}
              onChange={setSort}
              options={[
                { label: '按热门度', value: 'heat' },
                { label: '按上映时间', value: 'year' },
                { label: '按评分', value: 'rating' },
              ]}
            />
          </div>
        </div>

        {filtered.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {shown.map((movie) => (
                <ContentCard key={movie.id} item={movie} />
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center gap-3">
              {shown.length < filtered.length ? (
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-add-line text-[15px]"></i>
                  加载更多影片
                </button>
              ) : (
                <p className="flex items-center gap-2 font-label text-xs text-foreground-400">
                  <i className="ri-check-line text-[13px]"></i>
                  已显示全部 {filtered.length} 部影片
                </p>
              )}
              <p className="font-label text-[11px] text-foreground-400">
                已显示 {shown.length} / {filtered.length}
              </p>
            </div>
          </>
        ) : (
          <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-film-line text-[26px]"></i>
            </span>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">暂无符合条件的电影</h3>
            <p className="mt-2 text-sm text-foreground-500">试试换一个类型或年份，也可以重置全部筛选条件。</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-restart-line text-[15px]"></i>
              重置筛选
            </button>
          </div>
        )}
      </section>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <button
            type="button"
            aria-label="关闭筛选面板"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 cursor-default bg-foreground-950/45"
          />
          <div className="animate-fade-in relative ml-auto h-full w-[86%] max-w-sm overflow-y-auto bg-background-50 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-base font-semibold text-foreground-950">筛选电影</h3>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
              >
                <i className="ri-close-line text-[18px]"></i>
              </button>
            </div>
            <div className="mt-4">
              <FilterControls groups={filterGroups} onReset={resetFilters} showReset />
            </div>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full cursor-pointer rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}